import React, { useId } from "react";
import { createPortal } from "react-dom";
import BottomSheet from "./BottomSheet";
import "./InstantIntroSheet.css";
import { t } from "../i18n";

// Printed on the card art, like the word on a real card — the same in every
// language, so it is art, not copy.
const CARD_MARK = "DEBIT";

// "Instant payouts are here" (founder pick B, 1.5.2): the one-time sheet on
// Balance — the red DEBIT card from the talent tips emails with a gold
// "~30 MIN" chip, one title, one line, "Add debit card" / "Not now". Who sees
// it and when: utils/instantIntro + hooks/useInstantIntro. "Add debit card"
// does exactly what the Balance "Add card" row does (BalancePage).
//
// Portaled to <body>: the Balance page is a clipped one-screen box, so a fixed
// sheet inside it would stack under the tab bar. Nothing in it moves; with
// Reduce Motion the sheet fades in instead of sliding up (InstantIntroSheet.css).

const f = (n: number) => +n.toFixed(2);
const GOLD = "#F6C343";
const GOLD_D = "#D99A1E";
const RED = "#E5383B";
const RED_D = "#B3202A";
const RED_L = "#FF7477";
const BOLT = "M13.2 2 5 13.4h5l-1.6 8.6L17 10.4h-5.2Z";

/** The emails' debit card (talent tips scenes), drawn as crisp SVG. Credit-card ratio 1.586:1.
 *  `last4` prints the talent's own last four (the Balance popup); `mark` false
 *  drops the DEBIT word when instant money goes to a bank account. */
export function DebitCardArt({ width = 200, last4 = "4242", mark = true }: { width?: number; last4?: string; mark?: boolean }) {
  const id = `dc${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const w = width;
  const h = w / 1.586;
  const k = 17 / 24;
  const bx = w - 21;
  const by = 20;
  return (
    <svg className="iis-card-svg" viewBox={`0 0 ${f(w)} ${f(h)}`} width={f(w)} height={f(h)} aria-hidden="true">
      <defs>
        <clipPath id={`${id}c`}>
          <rect x="0" y="0" width={f(w)} height={f(h)} rx="12" />
        </clipPath>
        <linearGradient id={`${id}g`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#F04548" />
          <stop offset="1" stopColor={RED} />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width={f(w)} height={f(h)} rx="12" fill={`url(#${id}g)`} />
      <g clipPath={`url(#${id}c)`}>
        <path d={`M${f(w * 0.5)},0 L${f(w)},0 L${f(w)},${f(h * 0.62)} Z`} fill={RED_L} opacity="0.35" />
        <circle cx="0" cy={f(h)} r={f(h * 0.72)} fill={RED_D} opacity="0.6" />
      </g>
      {/* chip */}
      <rect x={f(w * 0.09)} y={f(h * 0.34)} width={f(w * 0.17)} height={f(h * 0.25)} rx="4" fill={GOLD} />
      <path
        d={`M${f(w * 0.09)},${f(h * 0.465)} h${f(w * 0.17)} M${f(w * 0.175)},${f(h * 0.34)} v${f(h * 0.25)}`}
        stroke={GOLD_D}
        strokeWidth="1.6"
      />
      {/* contactless */}
      {[0, 1, 2].map((i) => (
        <path
          key={i}
          d={`M${f(w * 0.33 + i * 6)},${f(h * 0.36)} q${f(4 + i * 2)},${f(h * 0.105)} 0,${f(h * 0.21)}`}
          fill="none"
          stroke="#ffffff"
          strokeWidth="2"
          strokeLinecap="round"
        />
      ))}
      {/* the bolt badge */}
      <circle cx={f(bx)} cy={f(by)} r="13" fill="#ffffff" />
      <path transform={`translate(${f(bx - 11 * k)},${f(by - 12 * k)}) scale(${f(k)})`} d={BOLT} fill={RED} />
      <text
        x={f(w * 0.09)}
        y={f(h * 0.86)}
        fontFamily="-apple-system, 'SF Pro Text', 'Helvetica Neue', Arial, sans-serif"
        fontWeight="700"
        fontSize="13"
        fill="#ffffff"
        letterSpacing="1"
      >
        {`•••• ${last4}`}
      </text>
      {/* Printed in English on real cards in every country (scheme rules) — card art, not copy. */}
      {mark && (
      <text
        x={f(w - 12)}
        y={f(h - 10)}
        fontFamily="'Arial Black', 'Helvetica Neue', Arial, sans-serif"
        fontWeight="900"
        fontSize="11"
        fill="#ffffff"
        textAnchor="end"
        letterSpacing="1.5"
      >
        {CARD_MARK}
      </text>
      )}
      <rect x="0.5" y="0.5" width={f(w - 1)} height={f(h - 1)} rx="11.5" fill="none" stroke="rgba(255,255,255,.18)" strokeWidth="1" />
    </svg>
  );
}

type Props = {
  /** Exactly the Balance "Add card" row's action: the in-app card sheet, or Stripe. */
  onAddCard: () => void;
  /** "Not now", the scrim — either way the sheet never comes back. */
  onClose: () => void;
};

export default function InstantIntroSheet({ onAddCard, onClose }: Props) {
  return createPortal(
    <BottomSheet open plain title={t("instantIntroSheet.title")} onClose={onClose} className="iis">
      <div className="iis-hero" aria-hidden="true">
        <div className="iis-card">
          <DebitCardArt width={200} />
          <span className="iis-chip">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M13.5 2 4.5 13.5H11L10 22l9-11.5h-6.5L13.5 2Z" />
            </svg>
            {t("instantIntroSheet.chip")}
          </span>
        </div>
      </div>
      <h3 className="iis-title">{t("instantIntroSheet.title")}</h3>
      <p className="iis-sub">{t("instantIntroSheet.sub")}</p>
      <button type="button" className="iis-cta" onClick={onAddCard}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
          <path d="M2.5 10h19M6.5 15h4" />
        </svg>
        {t("instantPayoutsPopup.addDebitCard")}
      </button>
      <button type="button" className="iis-ghost" onClick={onClose}>
        {t("instantPayoutsPopup.notNow")}
      </button>
    </BottomSheet>,
    document.body
  );
}
